/**
 * storagePaths.ts
 *
 * Helper pour générer et valider les chemins de stockage selon la convention
 * PR-06 : Storage paths standard + signed URLs
 */

/**
 * Convention de chemins :
 * PHASE 2: Les chemins retournés sont relatifs au bucket (sans préfixe du bucket)
 * - Uploads (privé) : {userId}/{projectId}/uploads/{filename}
 * - Thumbnails (privé + lecture publique si projet public) : {userId}/{projectId}/thumbnails/{filename}
 * - Assets réutilisables : {userId}/{filename}
 * - Templates publics : {templateId}/thumb.webp (public)
 * - Exports : {userId}/{projectId}/exports/{filename}
 *
 * Note: Le nom du bucket est spécifié lors de l'appel à .from('bucket-name')
 * Les politiques RLS utilisent (storage.foldername(name))[1] pour extraire userId,
 * donc le premier élément du chemin doit être userId (sauf pour templates publics)
 */

/**
 * Génère le chemin pour un upload de projet
 * P0.3 FIX: Retourne le chemin sans préfixe du bucket
 * Format: {userId}/{projectId}/uploads/{filename}
 */
export function getProjectUploadPath(userId: string, projectId: string, filename: string): string {
  return `${userId}/${projectId}/uploads/${filename}`;
}

/**
 * Helper explicite pour construire un chemin d'upload
 * Format: {userId}/{projectId}/uploads/{filename}
 */
export function buildUploadPath(userId: string, projectId: string, filename: string): string {
  return getProjectUploadPath(userId, projectId, filename);
}

/**
 * Génère le chemin pour un thumbnail de projet
 * P0.3 FIX: Retourne le chemin sans préfixe du bucket
 * Format: {userId}/{projectId}/thumbnails/{filename}
 */
export function getProjectThumbnailPath(userId: string, projectId: string): string {
  return `${userId}/${projectId}/thumbnails/thumb.webp`;
}

/**
 * Helper explicite pour construire un chemin de thumbnail
 * Format: {userId}/{projectId}/thumbnails/{filename}
 */
export function buildThumbnailPath(userId: string, projectId: string, filename: string): string {
  return `${userId}/${projectId}/thumbnails/${filename}`;
}

/**
 * Génère le chemin pour un asset réutilisable
 * P0.3 FIX: Retourne le chemin sans préfixe du bucket
 */
export function getUserAssetPath(userId: string, filename: string): string {
  return `${userId}/${filename}`;
}

/**
 * Génère le chemin pour un thumbnail de template public
 * P0.3 FIX: Retourne le chemin sans préfixe du bucket
 */
export function getPublicTemplateThumbnailPath(templateId: string): string {
  return `${templateId}/thumb.webp`;
}

/**
 * Génère le chemin pour un export
 * P0.3 FIX: Retourne le chemin sans préfixe du bucket
 * Format: {userId}/{projectId}/exports/{exportId}.{extension}
 */
export function getExportPath(
  userId: string,
  projectId: string,
  exportId: string,
  extension: string
): string {
  return `${userId}/${projectId}/exports/${exportId}.${extension}`;
}

/**
 * Helper explicite pour construire un chemin d'export
 * Format: {userId}/{projectId}/exports/{filename}
 */
export function buildExportPath(userId: string, projectId: string, filename: string): string {
  return `${userId}/${projectId}/exports/${filename}`;
}

/**
 * Valide un chemin de stockage
 * P0.3 FIX: Valide les chemins sans préfixe de bucket (relatifs au bucket)
 *
 * @param path - Le chemin à valider (sans préfixe de bucket)
 * @param expectedBucket - Le bucket attendu (optionnel, pour validation du format)
 * @returns true si le chemin est valide
 */
export function validateStoragePath(
  path: string,
  expectedBucket?: 'uploads' | 'thumbnails' | 'assets' | 'templates' | 'exports'
): boolean {
  // Vérifier que le chemin ne contient pas de caractères dangereux
  if (path.includes('..') || path.includes('//') || path.startsWith('/')) {
    return false;
  }

  // PHASE 2: Vérifier le format selon le bucket attendu (sans préfixe de bucket)
  if (expectedBucket) {
    const patterns = {
      uploads: /^[^/]+\/[^/]+\/uploads\/[^/]+$/, // userId/projectId/uploads/filename
      thumbnails: /^[^/]+\/[^/]+\/thumbnails\/[^/]+$/, // userId/projectId/thumbnails/filename
      assets: /^[^/]+\/[^/]+$/, // userId/filename
      templates: /^[^/]+\/thumb\.webp$/, // templateId/thumb.webp
      exports: /^[^/]+\/[^/]+\/exports\/[^/]+\.\w+$/, // userId/projectId/exports/exportId.ext
    };

    return patterns[expectedBucket].test(path);
  }

  // Vérification générale : doit être un chemin valide (au moins un segment)
  return path.length > 0 && !path.startsWith('/') && !path.endsWith('/');
}

/**
 * Extrait l'userId d'un chemin de stockage
 * P0.3 FIX: Extrait depuis un chemin sans préfixe de bucket
 */
export function extractUserIdFromPath(path: string): string | null {
  // Le premier segment est userId (sauf pour templates publics)
  const match = path.match(/^([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Extrait le projectId d'un chemin de stockage
 * P0.3 FIX: Extrait depuis un chemin sans préfixe de bucket
 */
export function extractProjectIdFromPath(path: string): string | null {
  // Le deuxième segment est projectId (pour uploads, thumbnails, exports)
  const match = path.match(/^[^/]+\/([^/]+)/);
  return match ? match[1] : null;
}

/**
 * Nettoie un nom de fichier pour éviter les caractères dangereux
 * Supprime les caractères spéciaux et normalise le nom
 */
export function sanitizeFilename(filename: string): string {
  // Extraire l'extension
  const lastDot = filename.lastIndexOf('.');
  const name = lastDot > 0 ? filename.slice(0, lastDot) : filename;
  const ext = lastDot > 0 ? filename.slice(lastDot) : '';

  // Nettoyer le nom : remplacer caractères spéciaux par underscore
  const sanitized = name
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_{2,}/g, '_') // Remplacer multiples underscores par un seul
    .replace(/^_+|_+$/g, ''); // Supprimer underscores en début/fin

  // Limiter la longueur (max 200 caractères pour le nom)
  const maxLength = 200;
  const finalName = sanitized.length > maxLength ? sanitized.slice(0, maxLength) : sanitized;

  return finalName + ext;
}

/**
 * Génère un nom de fichier unique avec UUID
 * Format: {uuid}.{ext}
 */
export function generateUuidFilename(ext: string): string {
  // Générer un UUID v4 simple
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  // Nettoyer l'extension
  const cleanExt = ext.startsWith('.') ? ext.slice(1) : ext;
  const safeExt = cleanExt.replace(/[^a-zA-Z0-9]/g, '');

  return `${uuid}.${safeExt}`;
}
